// Global Instructions Rule Applied!

import { DealTarget, MainState, DealsAction } from '../../interfaces/deals.ts'
import { DealFields } from '../../data_models/deal_record.ts'
import {
  SET_FILTER_OPTIONS,
  SET_DEALS,
  SET_ACTIVE_FILTERS,
  SET_ACTIVE_SORTING,
  CLEAR_ACTIVE_FILTERS,
  CLEAR_ACTIVE_SORTING,
  UPDATE_DEAL_RECORD,
  ADD_DEAL,
  UPDATE_DEAL_ROUND,
  REMOVE_DEAL_FROM_LIST
} from './constants.ts'
import { UPDATE_RECORD_BY_SEARCH } from '../../sync_engine/constants.ts'

const dealsReducer = (
  state: MainState = {
    pipeline: {
      filterOptions: {
        data: {}
      },
      activeFilters: [],
      activeSorting: [],
      data: []
    },
    deal: {
      filterOptions: {
        data: {}
      },
      activeFilters: [],
      activeSorting: [],
      data: []
    }
  },
  action: DealsAction
): MainState => {
  switch (action.type) {
    case SET_FILTER_OPTIONS:
      const { response: filterOptionsResponse, target: filterOptionsTarget } = action.payload

      if (filterOptionsTarget === DealTarget.PIPELINE) {
        return {
          ...state,
          pipeline: {
            ...state.pipeline,
            filterOptions: filterOptionsResponse
          }
        }
      } else if (filterOptionsTarget === DealTarget.DEAL) {
        return {
          ...state,
          deal: {
            ...state.deal,
            filterOptions: filterOptionsResponse
          }
        }
      }

      return state

    case SET_DEALS:
      const { response: dealsResponse, target: dealsTarget } = action.payload

      if (dealsTarget === DealTarget.DEAL) {
        return {
          ...state,
          deal: {
            ...state.deal,
            data: dealsResponse.data
          }
        }
      } else if (dealsTarget === DealTarget.PIPELINE) {
        return {
          ...state,
          pipeline: {
            ...state.pipeline,
            data: dealsResponse.data
          }
        }
      }

      return state

    case SET_ACTIVE_FILTERS:
      const { filters: activeFilters, target: filtersTarget } = action.payload

      if (filtersTarget === DealTarget.PIPELINE) {
        return {
          ...state,
          pipeline: {
            ...state.pipeline,
            activeFilters
          }
        }
      } else if (filtersTarget === DealTarget.DEAL) {
        return {
          ...state,
          deal: {
            ...state.deal,
            activeFilters
          }
        }
      }

      return state

    case SET_ACTIVE_SORTING:
      const { sorting: activeSorting, target: sortingTarget } = action.payload

      if (sortingTarget === DealTarget.PIPELINE) {
        return {
          ...state,
          pipeline: {
            ...state.pipeline,
            activeSorting
          }
        }
      } else if (sortingTarget === DealTarget.DEAL) {
        return {
          ...state,
          deal: {
            ...state.deal,
            activeSorting
          }
        }
      }

      return state

    case CLEAR_ACTIVE_FILTERS:
      const { target: clearFiltersTarget } = action.payload

      if (clearFiltersTarget === DealTarget.PIPELINE) {
        return {
          ...state,
          pipeline: {
            ...state.pipeline,
            activeFilters: []
          }
        }
      } else if (clearFiltersTarget === DealTarget.DEAL) {
        return {
          ...state,
          deal: {
            ...state.deal,
            activeFilters: []
          }
        }
      }

      return state

    case CLEAR_ACTIVE_SORTING:
      const { target: clearSortingTarget } = action.payload

      if (clearSortingTarget === DealTarget.PIPELINE) {
        return {
          ...state,
          pipeline: {
            ...state.pipeline,
            activeSorting: []
          }
        }
      } else if (clearSortingTarget === DealTarget.DEAL) {
        return {
          ...state,
          deal: {
            ...state.deal,
            activeSorting: []
          }
        }
      }

      return state

    case UPDATE_DEAL_RECORD:
      const { dealId, updates } = action.payload

      // Check if deal exists in state (check both deal and pipeline arrays)
      const dealExists =
        state.deal.data.some((deal) => deal.deals__id === dealId) ||
        state.pipeline.data.some((deal) => deal.deals__id === dealId)

      // If deal doesn't exist, leave it be (don't add it)
      if (!dealExists) {
        return state
      }

      // Check if updates contains the deal ID field - indicates a full record refresh
      const updatesIdField = updates[DealFields.deals__id] || updates.deals__id || updates.id
      const updateFieldCount = Object.keys(updates).length
      const isFullRecordRefresh =
        (updatesIdField === dealId || updatesIdField === String(dealId)) && updateFieldCount > 5

      // If it's a full record refresh, fully replace the deal (not merge)
      // Otherwise, merge updates for backward compatibility with partial updates
      return {
        ...state,
        deal: {
          ...state.deal,
          data: state.deal.data.map((deal) => {
            if (deal.deals__id === dealId) {
              return isFullRecordRefresh ? updates : { ...deal, ...updates }
            }
            return deal
          })
        },
        pipeline: {
          ...state.pipeline,
          data: state.pipeline.data.map((deal) => {
            if (deal.deals__id === dealId) {
              return isFullRecordRefresh ? updates : { ...deal, ...updates }
            }
            return deal
          })
        }
      }

    case ADD_DEAL:
      const { deal: newDeal } = action.payload

      return {
        ...state,
        deal: {
          ...state.deal,
          data: [newDeal, ...state.deal.data]
        },
        pipeline: {
          ...state.pipeline,
          data: [newDeal, ...state.pipeline.data]
        }
      }

    case UPDATE_DEAL_ROUND:
      const { roundId: dealRoundId, updates: dealRoundUpdates } = action.payload

      // Update deals where rounds__id matches the roundId
      const updateDealRoundInArray = (deals: any[]) =>
        deals.map((deal) => {
          // Check if this deal has the matching round ID
          if (deal[DealFields.rounds__id] === dealRoundId) {
            // Update all round-related fields
            return { ...deal, ...dealRoundUpdates }
          }
          return deal
        })

      return {
        ...state,
        deal: {
          ...state.deal,
          data: updateDealRoundInArray(state.deal.data)
        },
        pipeline: {
          ...state.pipeline,
          data: updateDealRoundInArray(state.pipeline.data)
        }
      }

    case REMOVE_DEAL_FROM_LIST: {
      const { dealId: removedDealId } = action.payload
      const idStr = String(removedDealId)
      const withoutDeal = (deals: any[]) =>
        deals.filter((deal) => String(deal[DealFields.deals__id] ?? deal.deals__id) !== idStr)
      return {
        ...state,
        deal: {
          ...state.deal,
          data: withoutDeal(state.deal.data)
        },
        pipeline: {
          ...state.pipeline,
          data: withoutDeal(state.pipeline.data)
        }
      }
    }

    case UPDATE_RECORD_BY_SEARCH: {
      const { searchField, searchValue, updates } = action.payload

      const updateDealBySearch = (deals: any[]) =>
        deals.map((record) => {
          const recordValue = record[searchField]
          const matches =
            recordValue === searchValue || String(recordValue) === String(searchValue) || recordValue == searchValue

          if (matches) {
            const filteredUpdates = Object.keys(updates).reduce(
              (acc, key) => {
                if (key in record) {
                  acc[key] = updates[key]
                }
                return acc
              },
              {} as Record<string, any>
            )

            if (Object.keys(filteredUpdates).length > 0) {
              return { ...record, ...filteredUpdates }
            }
          }
          return record
        })

      return {
        ...state,
        deal: {
          ...state.deal,
          data: updateDealBySearch(state.deal.data)
        },
        pipeline: {
          ...state.pipeline,
          data: updateDealBySearch(state.pipeline.data)
        }
      }
    }

    default:
      return state
  }
}

export default dealsReducer
export * from './deal_actions.ts'
export * from './deal_services.ts'
export * from './selectors.ts'
